import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import {
  Users2,
  Sparkles,
  Rss,
  MessageSquare,
  Star,
  Trophy,
  Compass,
  Heart,
  Flame,
  TrendingUp,
  Lock
} from 'lucide-react';

const feedFilters = [
  { value: 'feed', label: 'Feed', icon: Rss },
  { value: 'discussions', label: 'Discussions', icon: MessageSquare },
  { value: 'spotlights', label: 'Spotlights', icon: Star },
  { value: 'challenges', label: 'Challenges', icon: Trophy },
  { value: 'discovery', label: 'Discovery', icon: Compass },
  { value: 'engagement', label: 'Engagement', icon: Heart }
];

const previewFeed = [
  {
    id: 'cs1',
    avatarColor: 'pink',
    initials: 'JM',
    actor: 'Jenna M.',
    title: 'Creator Spotlight: fastest growing creator this week',
    summary: 'Featured creator hit 42k followers in 7 days using dual-platform livestreams.',
    type: { label: 'Spotlight', icon: Star, color: 'amber' },
    meta: 'Spotlights · 2h'
  },
  {
    id: 'dc1',
    avatarColor: 'blue',
    initials: 'OB',
    actor: 'Omar B.',
    title: 'Best OBS settings for TikTok + YouTube dual-streaming',
    summary: '12 replies · sharing bitrate + scene layouts that survive both platforms.',
    type: { label: 'Discussion', icon: MessageSquare, color: 'cyan' },
    meta: 'Discussions · 5h'
  },
  {
    id: 'ch1',
    avatarColor: 'yellow',
    initials: 'CH',
    actor: 'Consistency',
    title: 'Challenge: 7-day posting streak',
    summary: 'Post one piece of content per day for 7 days. Top streaks unlock badges.',
    type: { label: 'Challenge', icon: Trophy, color: 'yellow' },
    meta: 'Challenges · 1d'
  },
  {
    id: 'tc1',
    avatarColor: 'green',
    initials: 'PX',
    actor: 'PHOENYX',
    title: 'Trending creator — +18k followers this week',
    summary: 'Momentum spike from short-form clip repurposing across 3 platforms.',
    type: { label: 'Trending', icon: TrendingUp, color: 'green' },
    meta: 'Discovery · 1d'
  },
  {
    id: 'ms1',
    avatarColor: 'orange',
    initials: 'MR',
    actor: 'Maya R.',
    title: 'Hit a 30-day consistency streak 🔥',
    summary: 'Unlocked the "Iron Streak" badge after 30 days of daily posting.',
    type: { label: 'Milestone', icon: Flame, color: 'orange' },
    meta: 'Engagement · 2d'
  },
  {
    id: 'dc2',
    avatarColor: 'cyan',
    initials: 'AT',
    actor: 'Atlas T.',
    title: 'How are you batching content for the weekend?',
    summary: '8 replies · creators sharing weekend-batch workflows that scale.',
    type: { label: 'Discussion', icon: MessageSquare, color: 'cyan' },
    meta: 'Discussions · 3d'
  }
];

const FeedItem = ({ item, onSelect }) => {
  const TIcon = item.type.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="w-full flex items-start gap-3 px-4 py-2.5 border-b border-white/[0.04] last:border-0 text-left transition-colors hover:bg-white/[0.025] focus:bg-white/[0.04] focus:outline-none"
      data-testid={`community-feed-item-${item.id}`}
    >
      <div className={`w-8 h-8 rounded-full bg-${item.avatarColor}-500/15 border border-white/5 flex items-center justify-center shrink-0`}>
        <span className={`text-[10px] font-semibold text-${item.avatarColor}-300`}>{item.initials}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-white truncate">{item.actor}</span>
          <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded bg-${item.type.color}-500/10`}>
            <TIcon className={`h-2.5 w-2.5 text-${item.type.color}-400`} />
            <span className={`text-[9px] uppercase tracking-wide text-${item.type.color}-300`}>{item.type.label}</span>
          </div>
          <span className="text-[10px] text-[#555E6E] ml-auto shrink-0">{item.meta}</span>
        </div>
        <p className="text-sm text-[#C7CED9] truncate leading-snug mt-0.5">{item.title}</p>
        <p className="text-xs text-[#8A93A6] truncate leading-snug">{item.summary}</p>
      </div>
    </button>
  );
};

export default function ConnectCommunity() {
  const [filter, setFilter] = useState('feed');
  const [selected, setSelected] = useState(null);
  const activeFilterLabel = feedFilters.find((f) => f.value === filter)?.label || 'Feed';

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="connect-community-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Users2 className="h-6 w-6 text-blue-400" />
          <h1 className="text-3xl font-bold text-white">Community</h1>
          <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-300 uppercase tracking-wider font-semibold">
            Creator Ecosystem
          </Badge>
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Creator discovery, shared momentum, challenges, and community engagement.
        </p>
      </div>

      {/* Info Card */}
      <div
        className="mb-5 rounded-xl border border-white/5 bg-gradient-to-br from-blue-500/[0.05] via-transparent to-purple-500/[0.04] px-5 py-3.5"
        data-testid="community-info-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/[0.08] border border-blue-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-blue-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Creator Community Layer</p>
            <p className="text-xs text-[#8A93A6] leading-relaxed max-w-3xl">
              Community helps creators discover each other, participate in challenges, collaborate, and grow together inside the Consistency ecosystem.
            </p>
          </div>
        </div>
      </div>

      {/* Unified Community Feed */}
      <Card className="glass border-white/5 overflow-hidden" data-testid="community-feed">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 bg-[#0F1117]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0F1117]/80">
          <div className="flex items-center gap-2">
            <Rss className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-semibold text-white">Community Feed</span>
            <span className="text-xs text-[#8A93A6]">· {activeFilterLabel}</span>
          </div>

          <div className="flex flex-wrap items-center gap-1" data-testid="community-feed-filters">
            {feedFilters.map((f) => {
              const FIcon = f.icon;
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    filter === f.value
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      : 'text-[#8A93A6] hover:text-white border border-transparent'
                  }`}
                  data-testid={`community-feed-filter-${f.value}`}
                >
                  <FIcon className="h-3.5 w-3.5" />
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dense feed */}
        <div data-testid="community-feed-preview">
          {previewFeed.map((item) => (
            <FeedItem key={item.id} item={item} onSelect={setSelected} />
          ))}
        </div>

        {/* Compact empty state footer */}
        <div className="border-t border-white/5 px-5 py-4 bg-white/[0.015] flex items-center gap-3">
          <Lock className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
          <p className="text-xs text-[#8A93A6]">
            <span className="text-[#C7CED9] font-medium">Read-only preview.</span>{' '}
            Real community activity (posts, discussions, spotlights, challenges, discovery, milestones) will populate this feed as the ecosystem activates.
          </p>
        </div>
      </Card>

      <div className="mt-8 text-center">
        <p className="text-xs text-[#555E6E]">
          Community grows as creators participate — your ecosystem starts with consistency.
        </p>
      </div>

      {/* Read-only Detail Drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#0B0D12] border-l border-white/5 text-white" data-testid="community-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded bg-${selected.type.color}-500/10`}>
                    <selected.type.icon className={`h-3 w-3 text-${selected.type.color}-400`} />
                    <span className={`text-[10px] uppercase tracking-wide text-${selected.type.color}-300`}>{selected.type.label}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
                </div>
                <SheetTitle className="text-white">{selected.title}</SheetTitle>
                <SheetDescription className="text-[#8A93A6]">{selected.meta}</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-${selected.avatarColor}-500/15 border border-white/5 flex items-center justify-center`}>
                    <span className={`text-xs font-semibold text-${selected.avatarColor}-300`}>{selected.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{selected.actor}</p>
                    <p className="text-[11px] text-[#8A93A6]">Creator</p>
                  </div>
                </div>
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                  <p className="text-sm text-[#C7CED9] leading-relaxed">{selected.summary}</p>
                </div>
                <p className="text-xs text-[#555E6E] italic">
                  Full post body, replies, reactions, and interactions will render here once the community is live.
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
